import { Head, Link, router } from "@inertiajs/react"
import { useEffect, useMemo, useRef, useState } from "react"
import { Plus, Search, Tag, Trash2 } from "lucide-react"
import Pagination from "@/components/pagination.tsx"
import { CategoryFiltersType, CategoryType, PaginationType } from "./types.ts"

interface IndexProps {
  categories: CategoryType[]
  filters: CategoryFiltersType
  pagination: PaginationType
  can_create: boolean
  can_destroy?: boolean
}

export default function Index({ categories, filters, pagination, can_create, can_destroy = false }: IndexProps) {
  const hasInitialized = useRef(false)
  const [term, setTerm] = useState(filters.term)

  useEffect(() => {
    setTerm(filters.term)
  }, [filters.term])

  const query = useMemo(() => ({ term }), [term])

  useEffect(() => {
    if (!hasInitialized.current) {
      hasInitialized.current = true
      return
    }

    if (term === filters.term) {
      return
    }

    const timeoutId = setTimeout(() => {
      router.get("/admin/categories", { ...query, page: 1 }, {
        preserveState: true,
        preserveScroll: true,
        replace: true
      })
    }, 350)

    return () => clearTimeout(timeoutId)
  }, [query, term, filters.term])

  const handleDestroy = (category: CategoryType) => {
    if (!window.confirm(`Supprimer la catégorie "${category.name}" ?`)) return

    router.delete(`/admin/categories/${category.id}`, {
      preserveScroll: true,
    })
  }

  return (
    <>
      <Head title="Categories" />
      <div className="flex flex-col gap-6 p-8">
        {/* Header */}
        <div className="flex items-center justify-between">
          <div>
            <h1 className="text-3xl font-bold text-foreground">Les catégories</h1>
            <p className="text-sm text-muted-foreground">Gestion des catégories de taxes.</p>
          </div>
          {can_create && (
            <Link href="/admin/categories/new">
              <button className="flex items-center gap-2 rounded-lg bg-primary px-4 py-2 text-sm font-medium text-primary-foreground hover:bg-primary/90 transition-colors">
                <Plus className="h-4 w-4" />
                Ajouter une catégorie
              </button>
            </Link>
          )}
        </div>


        <div className="rounded-lg border border-border bg-card p-4">
          <div className="flex items-center gap-4">
            <div className="relative flex-1">
              <Search className="absolute left-3 top-1/2 h-4 w-4 -translate-y-1/2 text-muted-foreground" />

              <input placeholder="Rechercher les categories par nom ou code..."
                     value={term}
                     onChange={(e) => setTerm(e.target.value)}
                     className="w-full bg-transparent pl-10 text-sm text-foreground placeholder-muted-foreground focus:outline-none" />
            </div>
            <button className="rounded-lg border border-border px-3 py-2 text-sm font-medium text-foreground hover:bg-secondary transition-colors">Filter</button>
          </div>
        </div>



        {/* Categories Table */}
        <div className="rounded-lg border border-border bg-card overflow-hidden">
          <table className="w-full">
            <thead className="border-b border-border bg-secondary">
            <tr>
              <th className="px-6 py-3 text-left text-sm font-medium text-muted-foreground">Catégorie</th>
              <th className="px-6 py-3 text-left text-sm font-medium text-muted-foreground">Description</th>
              <th className="px-6 py-3 text-left text-sm font-medium text-muted-foreground">Statut</th>
              <th className="px-6 py-3 text-left text-sm font-medium text-muted-foreground">Actions</th>
            </tr>
            </thead>
            <tbody className="divide-y divide-border">
            {categories.map((category) => (
                <tr key={category.id} className="transition-colors hover:bg-secondary/50">
                  <td className="px-6 py-4">
                    <div className="flex items-center gap-3">
                      <div className="rounded-lg bg-primary/10 p-2">
                        <Tag className="h-4 w-4 text-primary" />
                      </div>
                      <div>
                        <p className="font-medium text-foreground">{category.name}</p>
                        <p className="text-xs text-muted-foreground font-mono">{category.code}</p>
                      </div>
                    </div>
                  </td>
                  <td className="px-6 py-4">
                    <p className="text-sm text-muted-foreground">{category.description}</p>
                  </td>
                  <td className="px-6 py-4">
                    <span className={`inline-flex rounded-full px-3 py-1 text-xs font-medium ${category.active ? "bg-success/10 text-success" : "bg-destructive/10 text-destructive"}`}>
                      {category.active ? "Actif" : "Inactif"}
                    </span>
                  </td>
                  <td className="px-6 py-4">
                    <Link href={`/admin/categories/${category.id}`}>
                      <button className="text-sm font-medium text-primary hover:text-primary/80 transition-colors">Voir les détails</button>
                    </Link>
                    {can_destroy && (
                      <button
                        type="button"
                        className="ml-2 inline-flex items-center rounded-md p-2 text-destructive hover:bg-destructive/10"
                        aria-label={`Supprimer ${category.name}`}
                        onClick={() => handleDestroy(category)}
                      >
                        <Trash2 className="h-4 w-4" />
                      </button>
                    )}
                  </td>
                </tr>
            ))}
            </tbody>
          </table>
        </div>

        <Pagination
          currentPage={pagination.current_page}
          totalPages={pagination.total_pages}
          basePath="/admin/categories"
          query={query}
        />
      </div>
    </>
  )
}
